import { uniq } from 'lodash'
import { InputRule } from 'prosemirror-inputrules'
import { MarkType, Mark } from 'prosemirror-model'
import { EditorState, TextSelection, Transaction } from 'prosemirror-state'

export const activeMarksOfType = (markType: MarkType, state: EditorState) => {
  let includedMarks: Array<Mark | undefined> = []

  const { selection } = state
  if (selection.empty) {
    const markCollection = state.storedMarks?.length ? state.storedMarks : selection.$from.marks() ?? []
    const mark = markType.isInSet(markCollection)

    if (mark) includedMarks.push(mark)
  } else {
    selection.ranges.forEach((range) => {
      const { $from, $to } = range,
        from = $from.pos,
        to = $to.pos

      state.doc.nodesBetween(from, to, (node) => {
        if (node.isText) {
          const mark = markType.isInSet(node.marks)
          if (mark !== null) includedMarks.push(mark)
        }
      })
    })
  }
  return includedMarks
}

export function activeMarkAttributes<T = string>(
  markList: Array<Mark | undefined>,
  attrName: string,
): Array<T | undefined> {
  return uniq(markList.map((mark) => mark?.attrs[attrName]))
}

// NOTE: the full match decides when this input rule is applied, the first
// group decides which part the mark should be added to (if present, otherwise full match)
// This helps having a "confirmation character"
export const addMarkInputRule = (regExp: RegExp, markOrMarkFunc: Mark | ((_match: Array<string>) => Mark)) =>
  new InputRule(regExp, (state, match, start, end) => {
    const mark = typeof markOrMarkFunc === 'function' ? markOrMarkFunc(match) : markOrMarkFunc

    const tr = state.tr.insertText(match[0], start, end)
    if (match[1]) {
      tr.addMark(start + match[0].indexOf(match[1]), start + match[1].length, mark) // only apply mark to a part of the text
    } else {
      tr.addMark(start, end + 1, mark)
    }

    return tr
  })

// Similar to addMarkInputRule, except it replaces the entire match with capture groups 1 and 2, keeps the toolbar state,
// and adds back any removed trailing whitespace. Capture groups 1 and 2 need to be adjacent.
// e.g. ttt(firstGroup)(secondGroup)ttt(otherGroups)ttt -> (firstGroup)(secondGroup)
// We use setSelection() + deleteSelection() instead of delete(), because using the latter causes the
// cursor to incorrectly get active marks while applying nested markdown styles. e.g. **~~test~~**
export const addMarkdownInputRule = (regExp: RegExp, markOrMarkFunc: Mark | ((_match: Array<string>) => Mark)) =>
  new InputRule(regExp, (state, match, start, end) => {
    const mark = typeof markOrMarkFunc === 'function' ? markOrMarkFunc(match) : markOrMarkFunc
    const tr: Transaction = state.tr
    const match1Offset = match[0].indexOf(match[1])
    const match2Offset = match[0].indexOf(match[2], match1Offset + 1)
    tr.addMark(start + match2Offset, start + match2Offset + match[2].length, mark)
    const prefixLength = match2Offset - (match1Offset + match[1].length)
    if (prefixLength !== 0) {
      tr.setSelection(TextSelection.create(tr.doc, start + match1Offset + match[1].length, start + match2Offset))
      tr.deleteSelection()
    }
    tr.setSelection(
      TextSelection.create(tr.doc, start + match2Offset + match[2].length - prefixLength, end - prefixLength),
    )
    tr.deleteSelection()
    if (match[0].endsWith(' ') && !match[2].endsWith(' ')) {
      tr.insertText(' ', start + match[1].length + match[2].length)
    }
    tr.removeStoredMark(mark)
    return tr
  })
